import type { AxiosRequestConfig } from 'axios';

import axiosInstance from './axiosCreator';
import type { Video } from '../types/Video';

export const MEDICAL_TOPIC_TREE_VIEWS = [
  'systems',
  'subjects',
  'specialties',
  'comlex',
] as const;

// The NCLEX-RN exam's own topic roots. Only nursing exams list these in their
// `allowedViews`; medical exams never do.
export const NURSING_TOPIC_TREE_VIEWS = [
  'nursing_subjects',
  'client_needs',
] as const;

// A partner course's own subtree. Only a COURSE exam lists it in its
// `allowedViews`; its root is the course's topic root (config `rootPath`).
export const COURSE_TOPIC_TREE_VIEWS = ['course'] as const;

export const TOPIC_TREE_VIEWS = [
  ...MEDICAL_TOPIC_TREE_VIEWS,
  ...NURSING_TOPIC_TREE_VIEWS,
  ...COURSE_TOPIC_TREE_VIEWS,
] as const;

export const MAX_TOPIC_SUMMARY_BATCH_SIZE = 32;
const MAX_CONCURRENT_TOPIC_SUMMARY_BATCHES = 3;

export type TopicTreeView = (typeof TOPIC_TREE_VIEWS)[number];
export type TopicTreeScope = 'exam' | 'global';

export type TopicTreeNode = {
  id: string;
  name: string;
  level: number;
  path: string;
  active: boolean;
  articleId: string | null;
  icon?: string;
  children?: TopicTreeNode[];
};

export type TopicTreeConfigResponse = {
  scope?: TopicTreeScope;
  /**
   * Whether this exact exam/global scope can use the bounded catalog contract.
   * Absent means a pre-catalog server or scope and keeps the legacy reader.
   */
  catalogEnabled?: boolean;
  examSlug: string;
  examId: string;
  allowedViews: TopicTreeView[];
  defaultView: TopicTreeView;
  view: TopicTreeView;
  rootPath: string;
};

export type TopicTreeExamConfigEntry = {
  slug: string;
  examId: string;
  allowedViews: TopicTreeView[];
  defaultView: TopicTreeView;
  specialtySlug?: string;
};

export type TopicContentCounts = {
  qbank: {
    total: number;
    new: number;
    due: number;
    scheduled: number;
    complete: number;
    // Started-but-not-submitted blocks for this topic. Optional so the client
    // tolerates older backends that don't yet send it.
    inProgress?: number;
  };
  flashcards: { total: number; new: number; due: number; scheduled: number };
  videos: { total: number; completed: number };
};

/**
 * Catalog totals describe published content availability. They deliberately do
 * not include a student's new/due/completed state, which is loaded separately
 * for the rendered topics.
 */
export type TopicCatalogCounts = {
  qbank: { total: number; available: boolean };
  // QBank actions exclude self-assessment seeds, while the legacy qbank total
  // remains seed-inclusive for summary/count compatibility.
  qbankSelectable?: { total: number; available: boolean };
  flashcards: { total: number; available: boolean };
  videos: { total: number; available: boolean };
  articles: { total: number; available: boolean };
};

export type TopicCatalogStatus = {
  readiness: 'ready' | 'pending' | 'unavailable';
  generationId: string | null;
  sourceRevision: string | null;
  isStale?: boolean;
  generationSourceRevision?: string | null;
  totalsByTopicId: Record<string, TopicCatalogCounts>;
};

export type TopicSummaryNode = {
  id: string;
  name: string;
  level: number;
  path: string;
  articleId: string | null;
  counts: TopicContentCounts;
  /**
   * The catalog confirms content exists, but the personal progress request
   * failed. Start flows can still ask the server to select eligible content.
   */
  personalCountsUnavailable?: boolean;
};

export type TopicSummaryBatchResponse = {
  topics: Record<string, TopicContentCounts>;
  timezone: string;
  catalog?: Pick<TopicCatalogStatus, 'readiness' | 'generationId'>;
};

export type TopicsBootstrapResponse = {
  nodes: TopicTreeNode[];
  sidebarNodes: TopicTreeNode[];
  currentTopicNode: TopicTreeNode | null;
  countsByTopicId: Record<string, TopicContentCounts>;
  timezone: string | null;
  catalog?: TopicCatalogStatus;
};

export type TopicSearchResult = {
  id: string;
  name: string;
  level: number;
  path: string;
  relativePath: string;
  articleId: string | null;
  hasChildren: boolean;
  view: TopicTreeView;
};

export type TopicSearchGroup = {
  view: TopicTreeView;
  total: number;
  results: TopicSearchResult[];
};

export type TopicSearchResponse = {
  currentView: TopicTreeView;
  groups: TopicSearchGroup[];
};

type ArticleDestination = {
  scope: TopicTreeScope;
  view: TopicTreeView;
  topicId: string;
  topicName: string;
  topicPath: string;
  nodePath: string;
  routePath: string;
};

type ArticleDestinationsResponse = {
  articleId: string;
  articleSlug: string | null;
  destinations: ArticleDestination[];
};

type LegacyTopicSearchResult = Omit<
  TopicSearchResult,
  'relativePath' | 'view'
> & {
  relativePath?: string;
  view?: TopicTreeView;
};

type LegacyTopicSearchResponse = {
  currentView?: TopicTreeView;
  results?: LegacyTopicSearchResult[];
};

type RawTopicSearchGroup = {
  view?: TopicTreeView;
  total?: number;
  results?: LegacyTopicSearchResult[];
};

type RawTopicSearchResponse =
  | TopicSearchResponse
  | LegacyTopicSearchResponse
  | {
      currentView?: TopicTreeView;
      groups?: RawTopicSearchGroup[];
      results?: LegacyTopicSearchResult[];
    };

const SEARCH_RESULT_VIEW_SEGMENTS: Record<string, TopicTreeView> = {
  systems: 'systems',
  subjects: 'subjects',
  specialties: 'specialties',
  comlex_supplement: 'comlex',
  comlex: 'comlex',
  nursing_subjects: 'nursing_subjects',
  client_needs: 'client_needs',
};

const isTopicTreeView = (value: unknown): value is TopicTreeView => {
  return (
    value === 'systems' ||
    value === 'subjects' ||
    value === 'specialties' ||
    value === 'comlex' ||
    value === 'nursing_subjects' ||
    value === 'client_needs'
  );
};

const deriveSearchResultView = (
  path?: string,
  fallbackView?: TopicTreeView
): TopicTreeView => {
  if (!path) {
    return fallbackView ?? 'systems';
  }

  const segments = path
    .split('.')
    .map((segment) => segment.trim().toLowerCase())
    .filter(Boolean);

  const viewSegment = segments[0] === 'root' ? segments[1] : segments[0];

  return SEARCH_RESULT_VIEW_SEGMENTS[viewSegment] ?? fallbackView ?? 'systems';
};

const deriveSearchRelativePath = (
  path?: string,
  fallbackView?: TopicTreeView
): string => {
  if (!path) {
    return '';
  }

  const segments = path
    .split('.')
    .map((segment) => segment.trim().toLowerCase())
    .filter(Boolean);

  if (segments.length === 0) {
    return '';
  }

  const startIndex = segments[0] === 'root' ? 2 : 1;
  const view = deriveSearchResultView(path, fallbackView);
  const rawViewSegment = segments[segments[0] === 'root' ? 1 : 0];

  const expectedSegment =
    view === 'comlex' ? 'comlex_supplement' : view.toLowerCase();

  const adjustedStartIndex =
    rawViewSegment === expectedSegment ||
    rawViewSegment === 'comlex' ||
    rawViewSegment === 'comlex_supplement'
      ? startIndex
      : segments[0] === 'root'
        ? 1
        : 0;

  return segments.slice(adjustedStartIndex).join('/');
};

const normalizeTopicSearchResult = (
  rawResult: LegacyTopicSearchResult,
  fallbackView?: TopicTreeView
): TopicSearchResult => {
  const resolvedView =
    rawResult.view ?? deriveSearchResultView(rawResult.path, fallbackView);

  return {
    id: rawResult.id,
    name: rawResult.name,
    level: rawResult.level,
    path: rawResult.path,
    relativePath:
      rawResult.relativePath ??
      deriveSearchRelativePath(rawResult.path, resolvedView),
    articleId: rawResult.articleId,
    hasChildren: rawResult.hasChildren,
    view: resolvedView,
  };
};

const normalizeTopicSearchResponse = (
  rawResponse: RawTopicSearchResponse,
  fallbackView?: TopicTreeView
): TopicSearchResponse => {
  const groupedResponse = rawResponse as {
    currentView?: TopicTreeView;
    groups?: RawTopicSearchGroup[];
  };

  const rawGroups = Array.isArray(groupedResponse.groups)
    ? groupedResponse.groups
    : null;

  if (rawGroups) {
    const normalizedGroups = rawGroups
      .filter((group): group is RawTopicSearchGroup => Boolean(group))
      .map((group) => {
        const groupView = isTopicTreeView(group.view)
          ? group.view
          : fallbackView;

        const normalizedResults = Array.isArray(group.results)
          ? group.results.map((result) =>
              normalizeTopicSearchResult(result, groupView)
            )
          : [];

        return {
          view:
            groupView ??
            normalizedResults[0]?.view ??
            fallbackView ??
            'systems',
          total:
            typeof group.total === 'number'
              ? group.total
              : normalizedResults.length,
          results: normalizedResults,
        };
      });

    return {
      currentView:
        (isTopicTreeView(groupedResponse.currentView)
          ? groupedResponse.currentView
          : undefined) ??
        fallbackView ??
        normalizedGroups[0]?.view ??
        'systems',
      groups: normalizedGroups,
    };
  }

  const legacyResponse = rawResponse as LegacyTopicSearchResponse;

  const normalizedLegacyResults = Array.isArray(legacyResponse.results)
    ? legacyResponse.results.map((result) =>
        normalizeTopicSearchResult(result, fallbackView)
      )
    : [];

  const currentView =
    (isTopicTreeView(legacyResponse.currentView)
      ? legacyResponse.currentView
      : undefined) ??
    fallbackView ??
    normalizedLegacyResults[0]?.view ??
    'systems';

  return {
    currentView,
    groups: [
      {
        view: currentView,
        total: normalizedLegacyResults.length,
        results: normalizedLegacyResults,
      },
    ],
  };
};

type TopicVideosBatchResponse = {
  topics: Record<string, Video[]>;
};

type TopicIconUpdateResponse = {
  id: string;
  icon: string | null;
};

export const fetchTopicTreeConfig = async (
  params: {
    examId?: string;
    view?: TopicTreeView;
    scope?: TopicTreeScope;
  } = {}
) => {
  try {
    const response = await axiosInstance.get('/api/v5/topics/config', {
      params,
    });

    return response.data as TopicTreeConfigResponse;
  } catch (error) {
    console.error('Error fetching topic tree config:', error);
    throw error;
  }
};

export const fetchGlobalTopicTree = async (
  params: {
    maxLevel?: number;
    examId?: string;
    view?: TopicTreeView;
    scope?: TopicTreeScope;
    parentId?: string;
    nodePath?: string;
    // 'qbank' prunes the tree to topics with at least one active QBank
    // question (backends without support ignore the param).
    contentScope?: 'all' | 'qbank';
  } = {}
) => {
  try {
    const response = await axiosInstance.get('/api/v5/topics/tree', {
      params,
    });

    return response.data as TopicTreeNode[];
  } catch (error) {
    console.error('Error fetching global topic tree:', error);
    throw error;
  }
};

export const fetchTopicsBootstrap = async (
  params: {
    maxLevel?: number;
    sidebarMaxLevel?: number;
    examId?: string;
    view?: TopicTreeView;
    scope?: TopicTreeScope;
    nodePath?: string;
    /**
     * Uses the bounded catalog reader and leaves personal counts for a later
     * visible-node request. Only send after the server-side catalog contract
     * is available.
     */
    includeCounts?: boolean;
  } = {},
  requestConfig?: Pick<AxiosRequestConfig, 'signal'>
) => {
  try {
    const response = await axiosInstance.get('/api/v5/topics/bootstrap', {
      params,
      ...requestConfig,
    });

    return response.data as TopicsBootstrapResponse;
  } catch (error) {
    console.error('Error fetching topics bootstrap:', error);
    throw error;
  }
};

export const fetchAvailableArticleDestinations = async (
  articleIdOrSlug: string,
  params: {
    examId?: string;
    currentView?: TopicTreeView;
    currentScope?: TopicTreeScope;
    currentNodePath?: string;
  } = {}
) => {
  try {
    const response = await axiosInstance.get(
      `/api/v5/topics/article-destinations/${encodeURIComponent(articleIdOrSlug)}`,
      {
        params,
      }
    );

    return response.data as ArticleDestinationsResponse;
  } catch (error) {
    console.error('Error fetching article destinations:', error);
    throw error;
  }
};

export const fetchTopicSummaryBatch = async ({
  topicIds,
  examId,
  generationId,
  signal,
}: {
  topicIds: string[];
  examId?: string;
  generationId?: string;
  signal?: AbortSignal;
}) => {
  if (topicIds.length > MAX_TOPIC_SUMMARY_BATCH_SIZE) {
    throw new Error(
      `Topic summary batches are limited to ${MAX_TOPIC_SUMMARY_BATCH_SIZE} topics.`
    );
  }

  try {
    const response = await axiosInstance.post(
      '/api/v5/topics/summary/batch',
      {
        topicIds,
        ...(examId ? { examId } : {}),
        ...(generationId ? { generationId } : {}),
      },
      {
        signal,
      }
    );

    return response.data as TopicSummaryBatchResponse;
  } catch (error) {
    console.error('Error fetching topic summary batch:', error);
    throw error;
  }
};

/**
 * Preserves complete summary results for legacy callers while ensuring each
 * server request stays within the catalog progress contract. It splits rather
 * than truncates, so a caller never mistakes omitted topics for zero counts.
 */
export const fetchTopicSummaryBatches = async ({
  topicIds,
  examId,
  generationId,
  signal,
  onBatch,
  onBatchError,
  continueOnError = false,
}: {
  topicIds: string[];
  examId?: string;
  generationId?: string;
  signal?: AbortSignal;
  /**
   * Called as each bounded request resolves. Consumers that render a growing
   * set of rows can publish available counts without waiting for the slowest
   * request in the group.
   */
  onBatch?: (response: TopicSummaryBatchResponse, topicIds: string[]) => void;
  /** Called for an individual failed batch when `continueOnError` is enabled. */
  onBatchError?: (error: unknown, topicIds: string[]) => void;
  /**
   * Continue loading independent batches after one fails. The default retains
   * the legacy fail-fast contract for existing callers.
   */
  continueOnError?: boolean;
}): Promise<TopicSummaryBatchResponse> => {
  const uniqueTopicIds = Array.from(new Set(topicIds));
  const batches: string[][] = [];

  for (
    let index = 0;
    index < uniqueTopicIds.length;
    index += MAX_TOPIC_SUMMARY_BATCH_SIZE
  ) {
    batches.push(
      uniqueTopicIds.slice(index, index + MAX_TOPIC_SUMMARY_BATCH_SIZE)
    );
  }

  if (batches.length === 0) {
    return { topics: {}, timezone: '' };
  }

  const responses: TopicSummaryBatchResponse[] = Array(batches.length);
  let nextBatchIndex = 0;

  const worker = async () => {
    while (nextBatchIndex < batches.length && !signal?.aborted) {
      const batchIndex = nextBatchIndex++;

      try {
        const response = await fetchTopicSummaryBatch({
          topicIds: batches[batchIndex],
          examId,
          generationId,
          signal,
        });

        responses[batchIndex] = response;
        onBatch?.(response, batches[batchIndex]);
      } catch (error) {
        if (!continueOnError || signal?.aborted) throw error;
        onBatchError?.(error, batches[batchIndex]);
      }
    }
  };

  await Promise.all(
    Array.from(
      {
        length: Math.min(MAX_CONCURRENT_TOPIC_SUMMARY_BATCHES, batches.length),
      },
      () => worker()
    )
  );

  return responses.filter(Boolean).reduce<TopicSummaryBatchResponse>(
    (combined, response) => ({
      topics: { ...combined.topics, ...response.topics },
      timezone: combined.timezone || response.timezone,
      catalog: response.catalog ?? combined.catalog,
    }),
    { topics: {}, timezone: '' }
  );
};

export const fetchTopicVideosBatch = async ({
  topicIds,
  examId,
}: {
  topicIds: string[];
  examId?: string;
}) => {
  try {
    const response = await axiosInstance.post('/api/v5/topics/videos/batch', {
      topicIds,
      ...(examId ? { examId } : {}),
    });

    return response.data as TopicVideosBatchResponse;
  } catch (error) {
    console.error('Error fetching topic videos batch:', error);
    throw error;
  }
};

export const fetchTopicSearch = async ({
  q,
  examId,
  view,
  scope,
  limit,
}: {
  q: string;
  examId?: string;
  view?: TopicTreeView;
  scope?: TopicTreeScope;
  limit?: number;
}) => {
  try {
    const response = await axiosInstance.get('/api/v5/topics/search', {
      params: {
        q,
        ...(examId ? { examId } : {}),
        ...(view ? { view } : {}),
        ...(scope ? { scope } : {}),
        ...(limit ? { limit } : {}),
      },
    });

    return normalizeTopicSearchResponse(
      response.data as RawTopicSearchResponse,
      view
    );
  } catch (error) {
    console.error('Error searching topics:', error);
    throw error;
  }
};

export const updateTopicNodeIcon = async ({
  topicId,
  icon,
}: {
  topicId: string;
  icon: string | null;
}) => {
  try {
    const response = await axiosInstance.patch(
      `/api/v5/topics/${topicId}/icon`,
      {
        icon,
      }
    );

    return response.data as TopicIconUpdateResponse;
  } catch (error) {
    console.error('Error updating topic icon:', error);
    throw error;
  }
};
